// mechanical.ts — "Sear & Tension", the MECHANICAL trap disarm minigame
// (plans/TRAP_DISARM_MINIGAME_PLAN_2026-07-31.md).
//
// The access plate is open: N spring-loaded SEARS on vertical slide tracks hold the striker back,
// and a TENSION needle shows how much strain your fiddling is putting on the spring. Each sear
// has a hidden catch point — drag it feeling for the judder (the lockpick feel-halo reskinned:
// inside cfg.geometry.feel_radius the handle shakes harder the closer you are) and release inside
// the catch to seat it. Overshooting past the catch feeds the needle; holding the RED band past
// the strain timer slips the striker and THE TRAP FIRES (shell.spring()). Seat every sear →
// submit. PERFECT (the owner's natural-20 ruling) = all seats with the needle never leaving
// green — the client reports the band honestly and the server owns everything else.
//
// DIFFICULTY IS SERVER-SIDE AND PER-CASTER (the lockpick rule): catch widths/positions,
// feel_radius, and strain_seconds all arrive from disarm_init_inc.php keyed on Trap DC and the
// Machining skill edge. No difficulty constants belong in this file — only feel.

import type { MechGeometry, TrapDisarmShell } from './trap-disarm';

// Tension bands as fractions of the 0–100 needle. TENSION_AMBER is also the PERFECT line: an
// attempt whose peak stays below it reports never_left_green and mints the synthetic natural 20.

// Tension plumbing (bands as FRACTIONS of the 0–100 needle; rates in units/second). These are
// feel, not difficulty — the difficulty lever is strain_seconds, which is per-caster server data.
// Exported so the rune board shares the identical PERFECT line. Two boards that mint the same
// synthetic natural 20 must not disagree by a point about what earns it.
export const TENSION_AMBER = 55;
export const TENSION_RED = 80;
// Every rate that used to live here (overshoot / movement / decay) is now MINTED PER CASTER by
// ft_trap_minigame_tension_model() and read through resolveTensionConfig — see the file header's
// rule that no difficulty constant belongs in this file. What is left is band positions and feel.
// Peak sideways throw of the feel judder, in px — the amplitude AT the catch, since the curve is
// clipped to 1 there. THE knob for "the handle shakes too much": it scales the whole halo
// uniformly, so turning it down changes how loud the tell is without touching the GRADIENT that
// makes the catch findable (that lives in feelStrength, and the skill ladder still gates it through
// feel_radius). 4 with the old linear ramp, which only ever touched full strength at dead centre;
// the bite curve holds near its peak across a much wider band, so the same number read as frantic.
// 2.8 was still too much (owner, 2026-08-03) — hence 1.6, a ~3px sweep at the sweet spot.
const JUDDER_PX = 1.6;

interface Sear {
    pos: number;          // 0 (bottom, rest) .. 100 (top)
    seated: boolean;
    springback: boolean;  // animating back to rest after a miss
}

export interface TensionConfig {
    holdPerSecond: number;    // creep while ANY sear is held — the trap never relaxes in your hand
    movePerUnit: number;
    ratchetPerFumble: number;
    ratchetPerExcessUnit: number;
    freeExcessPerDrag: number;
    floorReliefPerSeat: number;
    liveRetentionOnSeat: number;
    overshootRate: number;
    liveDecayRate: number;
    liveDecayDelay: number;   // seconds after the last real movement before live starts falling
}

/**
 * Read the minted tension model, falling back to the pre-2026-08-04 single-source needle for an
 * init payload minted before the rework (movement effectively inert, no ratchet, no relief). An old
 * cached payload should play like the old game, not like a broken new one.
 */
export function resolveTensionConfig(geo: Partial<MechGeometry>): TensionConfig {
    // null must take the fallback, not 0 — Number(null) is 0 and would silently mint a zeroed rate
    // if the server ever sent an explicit null rather than omitting the key.
    const num = (v: unknown, fallback: number): number => {
        if (v === null || v === undefined || v === '') return fallback;
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
    };
    return {
        holdPerSecond: num(geo.hold_live_per_second, 0),
        movePerUnit: num(geo.move_live_per_unit, 0.09),
        ratchetPerFumble: num(geo.ratchet_per_fumble, 0),
        ratchetPerExcessUnit: num(geo.ratchet_per_excess_unit, 0),
        freeExcessPerDrag: num(geo.free_excess_per_drag, 0),
        floorReliefPerSeat: num(geo.floor_relief_per_seat, 0),
        liveRetentionOnSeat: num(geo.live_retention_on_seat, 1),
        overshootRate: num(geo.overshoot_rate, 2.2),
        liveDecayRate: num(geo.live_decay_rate, 30),
        liveDecayDelay: num(geo.live_decay_delay, 0),
    };
}

/**
 * THE WHOLE TRAP'S AGITATION, as two components on one needle. Pure state and arithmetic, no DOM,
 * no timers — so tools/test/trap-disarm-tension-invariants.mjs can drive every tier/edge through
 * thousands of scripted attempts deterministically instead of inferring the model from pixels.
 *
 * FLOOR accumulates and is bought down only by SEATING. It never bleeds with time: this minigame
 * has no clock (strain_seconds governs only the red band), so a time-based bleed would let a
 * spammer reset for free by waiting — which is the exact behaviour the rework exists to punish.
 */
export class TensionSim {
    floor = 0;
    live = 0;
    max = 0;
    private sinceMove = Infinity;   // seconds since the last real handle movement

    constructor(private readonly cfg: TensionConfig) {}

    /** Combined needle, 0..100. */
    get value(): number {
        return Math.min(100, this.floor + this.live);
    }

    private mark(): void {
        if (this.value > this.max) this.max = this.value;
    }

    /** Handle actually moved `units` along its track (clamped travel, not raw cursor travel). */
    move(units: number): void {
        if (units <= 0) return;
        this.live += units * this.cfg.movePerUnit;
        this.sinceMove = 0;
        this.mark();
    }

    /**
     * One frame. `held` is whether a sear is in the player's hand at all; `overshoot` is how far
     * that handle sits above its catch.
     *
     * ⛔ WHILE A SEAR IS HELD THE TRAP NEVER RELAXES (owner, 2026-08-04: "once we grab a spring the
     * tension always creeps up automatically, so there is always a risk... at no point when
     * dragging should we not be gaining tension"). Holding is itself pressure on the mechanism, so
     * there is no such thing as a free pause mid-drag: dwelling to feel for a catch costs, sitting
     * still costs, and only letting go lets the trap settle. That also closes the last loophole in
     * the ratchet — you can no longer park a handle indefinitely while the needle bleeds down.
     *
     * Decay is therefore gated on HANDS OFF, not merely on "not overshooting".
     */
    step(dt: number, overshoot: number, held: boolean): void {
        this.sinceMove += dt;
        // A board with no hold model is a PRE-REWORK init payload, and there the old rule applies:
        // decay was gated on "not overshooting", not on hands-off. Without this the fallback would
        // freeze whatever movement had already accrued for the rest of a held drag — quieter than a
        // spike, but still not the old game (Codex review, 2026-08-04).
        const holdsTension = this.cfg.holdPerSecond > 0;
        if (held && holdsTension) {
            this.live += this.cfg.holdPerSecond * dt;
            if (overshoot > 0) this.live += overshoot * this.cfg.overshootRate * dt;
        } else if (overshoot > 0) {
            this.live += overshoot * this.cfg.overshootRate * dt;
        } else if (this.sinceMove >= this.cfg.liveDecayDelay) {
            this.live = Math.max(0, this.live - this.cfg.liveDecayRate * dt);
        }
        this.mark();
    }

    /**
     * A drag ended. `pathLength` is total travel, `directProgress` is |end - start|; the difference
     * beyond the free allowance is SAWING and ratchets the floor. Charging gross travel instead
     * would tax participation — a distant catch would cost more than a near one through no choice
     * of the player's — which is exactly what must not happen to keep PERFECT reachable.
     */
    endDrag(pathLength: number, directProgress: number, seated: boolean): void {
        const excess = Math.max(0, pathLength - directProgress - this.cfg.freeExcessPerDrag);
        this.floor += excess * this.cfg.ratchetPerExcessUnit;
        if (seated) {
            // Progress buys back safety — floor paid down, and most of the live vented so the drop
            // is visible even to a clean player who never accumulated any floor at all.
            this.floor = Math.max(0, this.floor - this.cfg.floorReliefPerSeat);
            this.live *= this.cfg.liveRetentionOnSeat;
        } else {
            this.floor += this.cfg.ratchetPerFumble;
        }
        this.floor = Math.min(100, this.floor);
        this.mark();
    }
}

/**
 * Judder strength at `dist` units from the catch centre — the whole "you have to overshoot to find
 * it" problem lives in this one curve, so it sits in one place rather than inline in the draw.
 *
 * The ORIGINAL ramp was `1 - dist / feel_radius`. At tier 6 the catch is ~3.5 units wide inside a
 * 45-unit halo, so crossing a whole catch-width changed the shake by under 8% — invisible. The only
 * sharp signal on the board was the tension needle, which starts climbing the instant you pass
 * `zone_hi`, so the dominant strategy became "shove each sear up until the needle moves, then back
 * off": deliberately driving into the danger band on every sear of every board to find the thing
 * the hint text tells you to approach carefully.
 *
 * So: a GAUSSIAN centred on the catch, about two catch-widths wide, over a 30% broad ramp. That
 * falls ~50% across the same catch — a gradient you can actually stop on — while the broad ramp
 * keeps distant guidance unchanged. `feel_radius` still gates the whole thing, so the per-caster
 * skill ladder is untouched: at a negative skill edge you feel it only within a few units and still
 * have to hunt, which is what the ladder is FOR.
 */
export function feelStrength(dist: number, halfWidth: number, feelRadius: number): number {
    if (dist >= feelRadius) return 0;
    const k = dist / (Math.max(0.75, halfWidth) * 1.9);
    return Math.min(1.15, (1 - dist / feelRadius) * 0.30 + Math.exp(-k * k) * 0.80);
}

export function mountMechanicalGame(stage: HTMLElement, shell: TrapDisarmShell): void {
    const geo = shell.cfg.geometry as MechGeometry;
    const sears: Sear[] = geo.sears.map(() => ({ pos: 0, seated: false, springback: false }));

    stage.innerHTML = '<canvas class="ft-trap-disarm-canvas" style="width:100%;touch-action:none;display:block;"></canvas>';
    const canvas = stage.querySelector('canvas') as HTMLCanvasElement;
    const cssW = Math.max(260, Math.min(420, stage.clientWidth || 320));
    const cssH = 250;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.height = cssH + 'px';
    const ctx = canvas.getContext('2d');
    if (!ctx) { shell.cancel(); return; }
    ctx.scale(dpr, dpr);

    shell.setHint('Feel for each catch — the judder sharpens as you close in. Handling the trap '
        + 'winds it, and a slip winds it further; seating a sear lets it back off. Never let the '
        + 'needle sit in the red.');

    // Layout: tracks across the left ~78%, the tension gauge on the right.
    const PAD = 14;
    const gaugeW = 30;
    const fieldW = cssW - PAD * 2 - gaugeW - 12;
    const laneW = fieldW / sears.length;
    const trackTop = 24;
    const trackBot = cssH - 26;
    const posToY = (pos: number): number => trackBot - (pos / 100) * (trackBot - trackTop);
    const yToPos = (y: number): number => Math.max(0, Math.min(100, ((trackBot - y) / (trackBot - trackTop)) * 100));
    const laneX = (i: number): number => PAD + laneW * i + laneW / 2;

    // Normalized ONCE, so the animation, the audio and the spring test can never disagree about how
    // long the strain window is. A malformed payload falls back to 0.35s — the untrained floor of
    // the server ladder, i.e. the harshest LEGITIMATE value, so a mangled duration can't hand anyone
    // an easier board. (A raw 0 would spring the instant the needle touched red; a raw NaN would
    // leave `strainT >= NaN` false forever and pin the needle in a red that never resolves.)
    // 0.35 is a FLOOR, not just a fallback: ft_trap_minigame_strain_seconds() bottoms out there at
    // edge <= -5, so clamping can only ever catch a value the server could not have produced.
    const strainSecondsRaw = Number(geo.strain_seconds);
    const strainSeconds = Number.isFinite(strainSecondsRaw)
        ? Math.max(0.35, strainSecondsRaw)
        : 0.35;

    // The striker bar, and the red bloom that crawls in behind it. Both gradients are built ONCE:
    // the striker's is drawn under a translate so its stops never move, and the bloom's geometry is
    // fixed with only its opacity varying, so neither needs re-allocating inside the rAF loop.
    const STRIKER_H = 12;
    const strikerGrad = ctx.createLinearGradient(0, 0, 0, STRIKER_H);
    strikerGrad.addColorStop(0, '#5a5a66');
    strikerGrad.addColorStop(0.45, '#3a3a44');
    strikerGrad.addColorStop(1, '#22222a');
    const bloomGrad = ctx.createRadialGradient(cssW / 2, cssH / 2, cssH * 0.24, cssW / 2, cssH / 2, cssH * 0.78);
    bloomGrad.addColorStop(0, 'rgba(220,40,40,0)');
    bloomGrad.addColorStop(1, 'rgba(220,40,40,1)');

    const sim = new TensionSim(resolveTensionConfig(geo));
    let tension = 0;
    // Eased needle. Rises INSTANTLY (threat is never softened) and falls over ~150ms, so the drop
    // when a sear seats reads as the mechanism settling rather than as a numeric discontinuity.
    let shownTension = 0;
    let strainT = 0;
    let dragging = -1;
    let dragPointerId = -1;
    let lastDragPos = 0;
    // Per-drag travel accounting: total path vs. how far the handle actually got. The difference is
    // sawing, and sawing is what ratchets the floor.
    let dragStartPos = 0;
    let dragPath = 0;
    let done = false;
    let lastTs = 0;
    let lastCreak = 0;      // strain cue, while the needle is red
    let lastCreakMove = 0;  // drag creak, while a sear is actually moving
    // ⚠ The three strain animations SPEED UP as the timer burns, and an accelerating oscillation
    // must accumulate its own phase from dt. Writing it the obvious way — sin(ts / period(sFrac)) —
    // looks right in a freshly-loaded demo and disintegrates in the real client: `ts` is time since
    // PAGE LOAD, so on an encounter tab that has been open an hour (ts ≈ 3.6e6) the d/dt of
    // ts/period(sFrac) is dominated by the period's own drift and runs to tens of kHz. The shudder
    // becomes aliased noise and the flash flips at random, both at a rate that depends on how long
    // the tab has been open. These accumulate instead, and reset with the timer.
    let shakePhase = 0;
    let flashPhase = 0;
    let bloomPhase = 0;

    const pointerPos = (ev: PointerEvent): { x: number; y: number } => {
        const r = canvas.getBoundingClientRect();
        return { x: (ev.clientX - r.left) * (cssW / (r.width || cssW)), y: (ev.clientY - r.top) * (cssH / (r.height || cssH)) };
    };

    canvas.addEventListener('pointerdown', (ev) => {
        // ⚠ One drag at a time. A second pointer — a touch, a stylus, a second mouse button — used
        // to overwrite `dragging` and the per-drag travel accounting mid-drag, abandoning the first
        // drag without ever reaching endDrag(). That was harmless while nothing was charged; with
        // the ratchet it is a free dodge (hunt a track with one finger, tap another sear to wipe
        // the debt). IGNORED rather than auto-released, because auto-releasing would charge a
        // fumble for a stray second finger — and the held drag keeps accruing anyway, so there is
        // nothing to gain by trying.
        if (done || dragging >= 0) return;
        const { x, y } = pointerPos(ev);
        for (let i = 0; i < sears.length; i++) {
            const s = sears[i];
            if (!s || s.seated) continue;
            const hx = laneX(i);
            const hy = posToY(s.pos);
            if (Math.abs(x - hx) < laneW / 2 && Math.abs(y - hy) < 26) {
                dragging = i;
                dragPointerId = ev.pointerId;
                lastDragPos = s.pos;
                dragStartPos = s.pos;
                dragPath = 0;
                s.springback = false;
                try { canvas.setPointerCapture(ev.pointerId); } catch { /* non-fatal */ }
                break;
            }
        }
    });

    canvas.addEventListener('pointermove', (ev) => {
        if (done || dragging < 0 || ev.pointerId !== dragPointerId) return;
        const { y } = pointerPos(ev);
        const s = sears[dragging];
        if (!s) return;
        const next = yToPos(y);

        // The spring you are fighting, made audible. 'creak' is ingested and sent by
        // ft_trap_minigame_sfx_urls() and was never played, so a dragged sear was silent until it
        // seated. Throttled and gated on real movement so it reads as a groan under load rather
        // than a rattle, and its PITCH RISES as you close on the catch — that is the audible half
        // of the feel halo, and the half that still works while your eyes are on the tension gauge.
        // Same feel_radius gate as the visual judder, so an untrained hand hears nothing until it
        // is nearly on top of the catch.
        const dragZoneNow = geo.sears[dragging];
        const now = performance.now();
        if (dragZoneNow && Math.abs(next - lastDragPos) > 0.6) {
            const zc = (dragZoneNow.zone_lo + dragZoneNow.zone_hi) / 2;
            const near = Math.max(0, 1 - Math.abs(next - zc) / Math.max(1, geo.feel_radius));
            // THE JUDDER, MADE AUDIBLE. A fixed 150ms tick at a fixed pitch is a metronome, and a
            // metronome is what "repetitive" means however many variants sit behind it. So the
            // creak carries the same shape the handle does: it TIGHTENS as you close on the catch
            // and its pitch WOBBLES, with the wobble growing on approach — so the tone itself
            // shakes rather than just recurring. Gated on feel_radius like the visual, so it stays
            // inside the skill ladder.
            //
            // Rate: 420ms out at the rim down to ~190ms on the catch. The first cut ran 220->90ms,
            // which at the catch was 11 creaks a second — that is a buzz, not a spring taking load
            // (owner, 2026-08-03). Even at the fast end this now stays slower than the ~4.7Hz
            // visual judder, which is what makes it read as the same object groaning rather than as
            // a separate rattle running alongside it.
            if (now - lastCreakMove > 420 - near * 230) {
                lastCreakMove = now;
                const wobble = Math.sin(now / 90) * 0.10 * near;
                shell.sfx.play(
                    'creak',
                    0.26 + near * 0.20,
                    0.90 + near * 0.55 + wobble + Math.random() * 0.06,
                );
            }
        }

        // Handling the trap at all agitates it — the needle answers the moment you move a handle,
        // not only once you have overshot a catch. Measured on the CLAMPED handle travel, so cursor
        // motion off the end of the track, springback and the visual judder all cost nothing.
        const travelled = Math.abs(next - lastDragPos);
        dragPath += travelled;
        sim.move(travelled);
        lastDragPos = next;
        s.pos = next;
    });

    const release = (): void => {
        if (done || dragging < 0) return;
        const i = dragging;
        dragging = -1;
        const s = sears[i];
        const zone = geo.sears[i];
        if (!s || !zone) return;
        const seated = s.pos >= zone.zone_lo && s.pos <= zone.zone_hi;
        sim.endDrag(dragPath, Math.abs(s.pos - dragStartPos), seated);
        if (seated) {
            s.seated = true;
            shell.sfx.play('click', 0.8);
            if (sears.every((x) => x.seated)) {
                done = true;
                shell.submit({
                    sear_positions: sears.map((x) => x.pos),
                    never_left_green: sim.max < TENSION_AMBER,
                });
            }
        } else {
            // A fumble WINDS THE TRAP. This is the whole point of the rework: retrying used to be
            // free, so machine-gunning each sear was strictly optimal. The ratchet is quiet but
            // permanent — only seating a sear pays it back.
            s.springback = true; // missed the catch — the spring takes it back
            // ⚠ NOT 'snap'. snap is the TRAP FIRING — a crash — and a fumble is not a catastrophe,
            // it is the same spring you have been groaning against taking its sear back. Playing a
            // quiet snap here both stole the failure cue's meaning and sounded broken: at rate 0.68
            // a short percussive sample resamples into audible mud (owner, 2026-08-04). 'creak' is
            // the right object, and 0.8 is close enough to unity to pitch down without distorting.
            shell.sfx.play('creak', 0.34, 0.78 + Math.random() * 0.05);
        }
    };
    // ⚠ Only the pointer that STARTED the drag may end it. Without the id check a second finger's
    // pointerup ends your drag and charges a fumble for a touch that never went near the handle —
    // punishing an accident, and on a touch device an easy one to have.
    const releaseFrom = (ev: PointerEvent): void => {
        if (dragging >= 0 && ev.pointerId !== dragPointerId) return;
        release();
    };
    canvas.addEventListener('pointerup', releaseFrom);
    canvas.addEventListener('pointercancel', releaseFrom);
    // Every OTHER way a drag can end must resolve too, or cancel-and-regrab becomes a free search
    // that dodges the ratchet: a lost capture, a touch stolen by the browser, alt-tab mid-drag.
    canvas.addEventListener('lostpointercapture', releaseFrom);
    window.addEventListener('blur', release);

    const frame = (ts: number): void => {
        if (done) return;
        if (!shell.stillMounted()) {
            // Panel replaced/removed under us (websocket repaint, navigation) — safe fail.
            done = true;
            shell.cancel(true);
            return;
        }
        const dt = lastTs > 0 ? Math.min(0.1, (ts - lastTs) / 1000) : 0.016;
        lastTs = ts;

        // ── simulate ───────────────────────────────────────────────────────────
        const dragSear = dragging >= 0 ? sears[dragging] : undefined;
        const dragZone = dragging >= 0 ? geo.sears[dragging] : undefined;
        const overshoot = dragSear && dragZone ? Math.max(0, dragSear.pos - dragZone.zone_hi) : 0;
        sim.step(dt, overshoot, dragging >= 0);
        tension = sim.value;
        shownTension = tension > shownTension
            ? tension
            : shownTension + (tension - shownTension) * Math.min(1, dt * 7);

        if (tension >= TENSION_RED) {
            strainT += dt;
            const sf = Math.min(1, strainT / strainSeconds);
            // Every tell tightens together as the timer burns. Periods in ms, so the phase advances
            // by dt-in-ms over the current period — see the note where these are declared.
            const dtMs = dt * 1000;
            shakePhase += dtMs / (18 - sf * 11);
            flashPhase += dtMs / (110 - sf * 82);
            bloomPhase += dtMs / (70 - sf * 40);
            // The audible half of the tell, and the one that works while your eyes are on the sear:
            // the strain cue REPEATS FASTER and RISES IN PITCH as the timer burns — 450ms apart at
            // 0.9x when the red band is first touched, ~150ms apart at 1.35x by the time it goes.
            if (ts - lastCreak > (450 - sf * 300)) {
                shell.sfx.play('strain', 0.62 + sf * 0.30, 0.9 + sf * 0.45 + Math.random() * 0.08);
                lastCreak = ts;
            }
            if (strainT >= strainSeconds) {
                done = true;
                shell.spring(); // striker slips — the trap fires at the disarmer
                return;
            }
        } else {
            strainT = 0;
            shakePhase = 0;
            flashPhase = 0;
            bloomPhase = 0;
        }

        for (const s of sears) {
            if (s.springback) {
                s.pos = Math.max(0, s.pos - 260 * dt);
                if (s.pos <= 0) { s.pos = 0; s.springback = false; }
            }
        }

        draw(ts);
        requestAnimationFrame(frame);
    };

    const draw = (ts: number): void => {
        ctx.clearRect(0, 0, cssW, cssH);

        // Plate
                // SEMI-TRANSPARENT BASE, not opaque. The environment ground is painted behind this
        // canvas (see paintTrapGround in trap-disarm.ts); an opaque fill here would hide it
        // completely. The scrim still has to carry the mechanism's contrast, which is built
        // for a near-black base. Reviewed at gameplay scale on 2026-08-04 (the handoff's gotcha 2):
        // at 0.72 the rock read as a SURFACE and the thin sear tracks washed out against it, so the
        // scrim is heavy enough that the ground reads as grain here. The environment is shown at
        // full strength by the frame around the canvas instead — see .ft-trap-ground's padding.
        ctx.fillStyle = 'rgba(29, 29, 34, 0.88)';
        ctx.fillRect(0, 0, cssW, cssH);

        const tFrac = Math.min(1, tension / 100);
        // How far through the hidden strain timer we are. 0 unless the needle is in the red.
        const sFrac = Math.min(1, strainT / strainSeconds);

        // ── the striker ───────────────────────────────────────────────────────────────────────
        // The bar every sear is holding back. It creeps down as tension climbs — the tension needle
        // made physical — and once the strain timer is running it drives the rest of the way toward
        // the sears, so its travel IS the countdown. Coarse by design: a frame-accurate readout
        // would turn riding the red into a safe binary search for the top of the hidden catch.
        //
        // Drawn BEFORE the handles, unlike the bench (which draws it last so its teeth overhang the
        // sprung coils it has that this flat version does not). At red entry the bar sits right on
        // top of a handle dragged high — which is exactly the handle you are holding, and exactly
        // when its judder matters most. Nothing here needs to overhang, so the handles win.
        const shake = strainT > 0 ? Math.sin(shakePhase) * (1.6 + sFrac * 4.5) : 0;
        const strikerY = trackTop - 10 + tFrac * 16 + sFrac * 26;
        const stX = PAD - 4 + shake;
        const stW = fieldW + 8;
        ctx.save();
        ctx.translate(0, strikerY - STRIKER_H / 2);
        ctx.fillStyle = strikerGrad;
        ctx.beginPath();
        ctx.roundRect(stX, 0, stW, STRIKER_H, 3);
        ctx.fill();
        ctx.fillStyle = '#2b2b34';
        for (let i = 0; i < sears.length; i++) {
            const tx = laneX(i) + shake;
            const tw = Math.min(12, laneW * 0.3);
            ctx.beginPath();
            ctx.moveTo(tx - tw / 2, STRIKER_H - 1);
            ctx.lineTo(tx + tw / 2, STRIKER_H - 1);
            ctx.lineTo(tx, STRIKER_H + 6);
            ctx.closePath();
            ctx.fill();
        }
        if (strainT > 0) {
            // Heat along the bar as the slip closes in.
            ctx.strokeStyle = `rgba(229,72,77,${(0.25 + sFrac * 0.6).toFixed(3)})`;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.roundRect(stX, 0, stW, STRIKER_H, 3);
            ctx.stroke();
        }
        ctx.restore();

        for (let i = 0; i < sears.length; i++) {
            const s = sears[i];
            const zone = geo.sears[i];
            if (!s || !zone) continue;
            const x = laneX(i);

            // Track channel
            ctx.strokeStyle = '#3a3a42';
            ctx.lineWidth = 6;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(x, trackTop);
            ctx.lineTo(x, trackBot);
            ctx.stroke();

            // Feel judder: amplitude grows as the handle nears the (hidden) catch center, inside
            // the per-caster feel radius. This is the ONLY tell — the zone itself never renders.
            let jitter = 0;
            const center = (zone.zone_lo + zone.zone_hi) / 2;
            const dist = Math.abs(s.pos - center);
            if (!s.seated && dragging === i && dist < geo.feel_radius) {
                const halfW = (zone.zone_hi - zone.zone_lo) / 2;
                const strength = feelStrength(dist, halfW, geo.feel_radius);
                // Clipped at 1. feelStrength deliberately overshoots to 1.15 right around the catch
                // (the Gaussian riding on top of the broad ramp), which made the SWEET SPOT also the
                // most violent point on the track — the opposite of what it should feel like. The
                // clip costs nothing as a locator: everything above 1.0 is a narrow plateau, so the
                // gradient either side of it is untouched, and feelStrength itself is left alone so
                // the parity/smoke numbers still measure the real curve.
                const amp = Math.min(1, strength);
                // Harshness now RAMPS IN with how far past the centre you are, rather than snapping
                // on the instant you cross it. "Gone too far" is a matter of degree, and at the
                // catch itself the handle should feel settled rather than frantic.
                const over = Math.max(0, Math.min(1, (s.pos - center) / Math.max(0.75, halfW)));
                //
                // ⚠ Harsh means a different SHAPE, not a higher rate. An earlier cut used a 61Hz
                // component to sound rough; a 60fps canvas cannot draw 61Hz, so it aliased into
                // random per-frame jitter and the handle read as broken rather than as buzzing.
                // Both rates here stay well under half the frame rate and the roughness comes from
                // squaring the wave off. (sin(ts / k) has period 2*pi*k ms: k=34 is ~4.7Hz, k=13
                // ~12Hz. Anything past ~k=5 is unrenderable and will alias.)
                const smooth = Math.sin(ts / 34 + i * 7);
                const buzz = Math.sin(ts / 13 + i * 7);
                const harsh = Math.sign(buzz) * Math.pow(Math.abs(buzz), 0.45);
                jitter = (smooth * (1 - over) + harsh * over) * amp * JUDDER_PX;
            }

            // Handle
            const y = posToY(s.pos);
            ctx.fillStyle = s.seated ? '#3f9d5a' : dragging === i ? '#c9a44a' : '#8b8b96';
            ctx.beginPath();
            const hw = Math.min(20, laneW * 0.42);
            ctx.roundRect(x - hw / 2 + jitter, y - 9, hw, 18, 4);
            ctx.fill();
            if (s.seated) {
                ctx.strokeStyle = '#67d98a';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        }

        // Tension gauge — green / amber / red, needle line, red-band strain flash.
        const gx = cssW - PAD - gaugeW;
        const gTop = trackTop;
        const gBot = trackBot;
        const bandY = (v: number): number => gBot - (v / 100) * (gBot - gTop);
        ctx.fillStyle = '#295c39';
        ctx.fillRect(gx, bandY(TENSION_AMBER), gaugeW, gBot - bandY(TENSION_AMBER));
        ctx.fillStyle = '#8a6d2a';
        ctx.fillRect(gx, bandY(TENSION_RED), gaugeW, bandY(TENSION_AMBER) - bandY(TENSION_RED));
        // The red band's flash ACCELERATES as the timer burns — ~110ms per phase when the needle
        // first touches red, down to ~28ms by the time the striker is about to slip. A fixed blink
        // says "straining" forever and never says "now".
        const hot = strainT > 0 && Math.floor(flashPhase) % 2 === 0;
        ctx.fillStyle = hot ? '#e5484d' : '#7d2a2e';
        ctx.fillRect(gx, gTop, gaugeW, bandY(TENSION_RED) - gTop);

        // The RESTING LINE: where the needle will settle once the live component has bled off —
        // i.e. the damage already done to this trap. Without it, a needle that stops falling part
        // way down reads as a stuck gauge rather than as accumulated harm. It is a mark, not a
        // meter: no number, no countdown, nothing that could be binary-searched against.
        if (sim.floor > 1) {
            const fy = bandY(Math.min(100, sim.floor));
            ctx.strokeStyle = 'rgba(255,170,90,.85)';
            ctx.lineWidth = 1.5;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo(gx, fy);
            ctx.lineTo(gx + gaugeW, fy);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        const ny = bandY(shownTension);
        ctx.strokeStyle = '#f2f2f5';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(gx - 4, ny);
        ctx.lineTo(gx + gaugeW + 4, ny);
        ctx.stroke();

        // ── the imminent-bang tell ────────────────────────────────────────────────────────────
        // A red bloom crawling in from the panel edges, scaled by how much of the strain timer is
        // gone. Peripheral and preattentive: it registers while your eyes are on the sear you are
        // dragging, which is exactly where they are when this matters.
        if (strainT > 0) {
            const pulse = 0.72 + 0.28 * Math.sin(bloomPhase);
            ctx.save();
            ctx.globalAlpha = sFrac * 0.60 * pulse;
            ctx.fillStyle = bloomGrad;
            ctx.fillRect(0, 0, cssW, cssH);
            ctx.restore();
        }

        ctx.fillStyle = '#9a9aa4';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('TENSION', gx + gaugeW / 2, gBot + 14);
        ctx.fillText(`${sears.filter((s) => s.seated).length} / ${sears.length} seated`, PAD + fieldW / 2, gBot + 14);
    };

    requestAnimationFrame(frame);
}
